import { BarChart3, CalendarRange, Flag, Pencil, Play, Plus, Square } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { confirmModal, Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../../shared/format'
import { SPRINT_PERMS, useCloseSprint, useSaveSprint, useSprintReport, useSprints, useStartSprint, type Sprint, type SprintInput } from '../../sprintApi'
import { todayIso } from '../../taskFilters'
import type { Project } from '../../types'
import { toastError, useStaffLookup } from '../../ui'

const STATUS_TONE = { planned: 'info', active: 'success', closed: 'neutral' } as const

/** Sprintler: tarih aralığı, hedef, personel ve kapasite; başlatma (projede tek etkin), kapatma (bitmeyenler taşınır) ve rapor. */
export function SprintsTab({ project }: { project: Project }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const q = useSprints(project.id)
  const start = useStartSprint(project.id)
  const [editing, setEditing] = useState<Sprint | 'new' | null>(null)
  const [closing, setClosing] = useState<Sprint | null>(null)
  const [report, setReport] = useState<string | null>(null)
  const manage = can(SPRINT_PERMS.manage)
  const items = q.data ?? []

  const onStart = async (s: Sprint) => {
    if (!(await confirmModal({ title: t('projects.sprints.startTitle'), description: t('projects.sprints.startBody', { name: s.name }), confirmLabel: t('projects.sprints.start') }))) return
    start.mutate(s.id, { onSuccess: () => toast.success(t('projects.sprints.started')), onError: toastError })
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{t('projects.sprints.hint')}</p>
        {manage ? (
          <Button onClick={() => setEditing('new')}>
            <Plus />
            {t('projects.sprints.new')}
          </Button>
        ) : null}
      </div>
      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : q.isLoading ? (
        <Skeleton className="h-40 rounded-xl" />
      ) : !items.length ? (
        <EmptyState icon={CalendarRange} title={t('projects.sprints.empty')} />
      ) : (
        <div className="space-y-3">
          {items.map((s) => {
            const pct = s.tasks ? Math.round((s.doneTasks / s.tasks) * 100) : 0
            return (
              <section key={s.id} className="rounded-xl border bg-card shadow-card">
                <div className="flex flex-wrap items-start gap-3 p-4">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold">{s.name}</h3>
                      <Badge tone={STATUS_TONE[s.status]}>{t(`projects.sprints.status.${s.status}`)}</Badge>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {fmtDate(s.startDate)} – {fmtDate(s.endDate)}
                      </span>
                    </div>
                    {s.goal ? (
                      <p className="flex items-start gap-1.5 text-sm text-muted-foreground">
                        <Flag className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                        {s.goal}
                      </p>
                    ) : null}
                    <div className="flex items-center gap-2 pt-1">
                      <div className="h-2 w-48 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs tabular-nums text-muted-foreground">{t('projects.sprints.progress', { done: s.doneTasks, total: s.tasks })}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant={report === s.id ? 'default' : 'outline'} onClick={() => setReport(report === s.id ? null : s.id)}>
                      <BarChart3 />
                      {t('projects.sprints.report')}
                    </Button>
                    {manage && s.status !== 'closed' ? (
                      <Button size="sm" variant="outline" onClick={() => setEditing(s)}>
                        <Pencil />
                        {t('common.edit')}
                      </Button>
                    ) : null}
                    {manage && s.status === 'planned' ? (
                      <Button size="sm" onClick={() => void onStart(s)} disabled={start.isPending}>
                        <Play />
                        {t('projects.sprints.start')}
                      </Button>
                    ) : null}
                    {manage && s.status !== 'closed' ? (
                      <Button size="sm" variant="outline" onClick={() => setClosing(s)}>
                        <Square />
                        {t('projects.sprints.close')}
                      </Button>
                    ) : null}
                  </div>
                </div>
                {report === s.id ? <SprintReportPanel id={s.id} /> : null}
              </section>
            )
          })}
        </div>
      )}
      {editing ? <SprintFormModal project={project} sprint={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
      {closing ? <CloseSprintModal project={project} sprint={closing} others={items.filter((x) => x.id !== closing.id && x.status !== 'closed')} onClose={() => setClosing(null)} /> : null}
    </div>
  )
}

function SprintReportPanel({ id }: { id: string }) {
  const { t } = useTranslation()
  const { nameOf } = useStaffLookup()
  const q = useSprintReport(id)
  if (q.isLoading) return <Skeleton className="m-4 h-32" />
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />
  const r = q.data
  const tiles: [string, string][] = [
    [t('projects.sprints.tiles.tasks'), `${r.doneTasks} / ${r.tasks}`],
    [t('projects.sprints.tiles.hours'), `${r.doneEstimated} / ${r.estimated} sa`],
    [t('projects.sprints.tiles.spent'), `${r.spent} sa`],
    [t('projects.sprints.tiles.added'), String(r.addedAfterStart)],
    [t('projects.sprints.tiles.unassigned'), String(r.unassigned)],
  ]
  return (
    <div className="space-y-3 border-t p-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {tiles.map(([k, v]) => (
          <div key={k} className="rounded-lg border bg-muted/20 p-3">
            <p className="text-xs text-muted-foreground">{k}</p>
            <p className="text-lg font-semibold tabular-nums">{v}</p>
          </div>
        ))}
      </div>
      {r.members.length ? (
        <div className="overflow-x-auto">
          <Table>
            <THead>
              <TR>
                <TH>{t('projects.sprints.member')}</TH>
                <TH className="text-right">{t('projects.sprints.capacity')}</TH>
                <TH className="text-right">{t('projects.sprints.assignedHours')}</TH>
                <TH className="text-right">{t('projects.sprints.doneHours')}</TH>
                <TH className="text-right">{t('projects.sprints.spentHours')}</TH>
                <TH className="text-right">{t('projects.sprints.tiles.tasks')}</TH>
              </TR>
            </THead>
            <TBody>
              {r.members.map((m) => (
                <TR key={m.userId}>
                  <TD className="font-medium">
                    {nameOf(m.userId)}
                    {m.over ? (
                      <Badge tone="danger" className="ml-2">
                        {t('projects.sprints.over')}
                      </Badge>
                    ) : null}
                  </TD>
                  <TD className="text-right tabular-nums">{m.capacityHours ?? '—'}</TD>
                  <TD className={cn('text-right tabular-nums', m.over && 'font-semibold text-destructive')}>{m.estimated}</TD>
                  <TD className="text-right tabular-nums">{m.doneEstimated}</TD>
                  <TD className="text-right tabular-nums">{m.spent}</TD>
                  <TD className="text-right tabular-nums">
                    {m.doneTasks} / {m.tasks}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      ) : null}
    </div>
  )
}

function SprintFormModal({ project, sprint, onClose }: { project: Project; sprint: Sprint | null; onClose: () => void }) {
  const { t } = useTranslation()
  const { nameOf } = useStaffLookup()
  const save = useSaveSprint(project.id)
  const today = todayIso()
  const [v, setV] = useState<SprintInput>({
    name: sprint?.name ?? '',
    goal: sprint?.goal ?? '',
    startDate: sprint?.startDate ?? today,
    endDate: sprint?.endDate ?? today,
    members: sprint?.members ?? project.team.map((m) => ({ userId: m.userId, capacityHours: null })),
  })
  const set = (p: Partial<SprintInput>) => setV((x) => ({ ...x, ...p }))
  const member = (userId: string) => v.members.find((m) => m.userId === userId)
  const toggle = (userId: string, on: boolean) => set({ members: on ? [...v.members, { userId, capacityHours: null }] : v.members.filter((m) => m.userId !== userId) })
  const setCap = (userId: string, raw: string) =>
    set({ members: v.members.map((m) => (m.userId === userId ? { ...m, capacityHours: raw === '' ? null : Math.max(0, Number(raw) || 0) } : m)) })
  const ok = v.name.trim().length > 0 && !!v.startDate && !!v.endDate && v.endDate >= v.startDate
  return (
    <Modal
      open
      size="lg"
      onOpenChange={(o) => !o && onClose()}
      icon={<CalendarRange />}
      title={sprint ? t('projects.sprints.editTitle', { name: sprint.name }) : t('projects.sprints.new')}
      description={t('projects.sprints.formHint')}
      footer={
        <>
          <Button variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!ok || save.isPending}
            onClick={() =>
              save.mutate(
                { id: sprint?.id, input: v },
                {
                  onSuccess: () => {
                    toast.success(t('projects.sprints.saved'))
                    onClose()
                  },
                  onError: toastError,
                },
              )
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="sp-name">{t('projects.sprints.name')}</Label>
            <Input id="sp-name" value={v.name} maxLength={80} onChange={(e) => set({ name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sp-start">{t('projects.fields.startDate')}</Label>
            <DateField id="sp-start" value={v.startDate} onChange={(startDate) => set({ startDate })} clearable={false} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sp-end">{t('projects.sprints.endDate')}</Label>
            <DateField id="sp-end" value={v.endDate} min={v.startDate || undefined} onChange={(endDate) => set({ endDate })} clearable={false} />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="sp-goal">{t('projects.sprints.goal')}</Label>
          <Textarea id="sp-goal" rows={2} maxLength={4000} value={v.goal} onChange={(e) => set({ goal: e.target.value })} />
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium">{t('projects.sprints.members')}</p>
          {!project.team.length ? <p className="text-xs text-muted-foreground">{t('projects.sprints.noTeam')}</p> : null}
          <ul className="space-y-1.5">
            {project.team.map((m) => {
              const sel = member(m.userId)
              return (
                <li key={m.userId} className="flex items-center gap-3 rounded-lg border px-3 py-2 text-sm">
                  <input type="checkbox" className="size-4 accent-[var(--color-primary)]" checked={!!sel} onChange={(e) => toggle(m.userId, e.target.checked)} aria-label={nameOf(m.userId)} />
                  <span className="min-w-0 flex-1 truncate">
                    {nameOf(m.userId)} <span className="text-xs text-muted-foreground">{m.title}</span>
                  </span>
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    className="h-8 w-28"
                    disabled={!sel}
                    value={sel?.capacityHours ?? ''}
                    placeholder={t('projects.sprints.capacity')}
                    aria-label={t('projects.sprints.capacity')}
                    onChange={(e) => setCap(m.userId, e.target.value)}
                  />
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </Modal>
  )
}

function CloseSprintModal({ project, sprint, others, onClose }: { project: Project; sprint: Sprint; others: Sprint[]; onClose: () => void }) {
  const { t } = useTranslation()
  const close = useCloseSprint(project.id)
  const [moveTo, setMoveTo] = useState(others.find((x) => x.status === 'planned')?.id ?? '')
  const open = sprint.tasks - sprint.doneTasks
  return (
    <Modal
      open
      size="md"
      onOpenChange={(o) => !o && onClose()}
      icon={<Square />}
      title={t('projects.sprints.closeTitle', { name: sprint.name })}
      description={t('projects.sprints.closeHint', { count: open })}
      footer={
        <>
          <Button variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={close.isPending}
            onClick={() =>
              close.mutate(
                { id: sprint.id, moveTo },
                {
                  onSuccess: (r) => {
                    toast.success(t('projects.sprints.closed', { count: Number((r as { moved?: number })?.moved ?? 0) }))
                    onClose()
                  },
                  onError: toastError,
                },
              )
            }
          >
            {t('projects.sprints.close')}
          </Button>
        </>
      }
    >
      <Select
        aria-label={t('projects.sprints.moveTo')}
        value={moveTo}
        onChange={setMoveTo}
        options={[{ value: '', label: t('projects.sprints.moveNone') }, ...others.map((x) => ({ value: x.id, label: x.name, description: t(`projects.sprints.status.${x.status}`) }))]}
      />
    </Modal>
  )
}
